'use client';

import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuidv4 } from 'uuid';
import { ref, uploadBytes } from 'firebase/storage';
import { Upload, FileImage, X, Loader2 } from 'lucide-react';

import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { storage } from '@/lib/firebase';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormField, FormMessage } from '@/components/ui/form';
import { ScrollArea } from '@/components/ui/scroll-area';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const formSchema = z.object({
  photos: z.array(z.instanceof(File)).min(1, 'Please select at least one photo.'),
});

interface PhotoUploadDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  eventId: string;
}

export function PhotoUploadDialog({ isOpen, setIsOpen, eventId }: PhotoUploadDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { photos: [] },
  });

  const { photos } = form.watch();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const validFiles = files.filter(f => f.size <= MAX_FILE_SIZE && ACCEPTED_IMAGE_TYPES.includes(f.type));

    if (files.length !== validFiles.length) {
      toast({
        variant: "destructive",
        title: "Selection filtered",
        description: "Some photos were over 10MB or an invalid format."
      });
    }

    form.setValue('photos', [...(photos || []), ...validFiles], { shouldValidate: true });
  };

  const removeFile = (index: number) => {
    const updated = [...photos];
    updated.splice(index, 1);
    form.setValue('photos', updated, { shouldValidate: true });
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!user) return;
    setIsUploading(true);
    try {
      const promises = values.photos.map((file) => {
        const ext = file.name.split('.').pop();
        const path = `events/${eventId}/users/${user.uid}/photo_uploads/${uuidv4()}.${ext}`;
        return uploadBytes(ref(storage, path), file);
      });
      await Promise.all(promises);
      toast({ title: 'Success', description: 'Your photos are being processed.' });
      setIsOpen(false);
      form.reset();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Upload Failed' });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !isUploading && setIsOpen(v)}>
      <DialogContent className="sm:max-w-[400px] gap-0 p-0 overflow-hidden">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle>Upload Photos</DialogTitle>
            <DialogDescription>Select the images you want to add to this event.</DialogDescription>
          </DialogHeader>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="px-6 pb-6 space-y-4">
              {/* Main Selection Area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="relative border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center bg-muted/30 hover:bg-muted/50 transition-all cursor-pointer group"
              >
                <div className="bg-background p-3 rounded-full shadow-sm mb-3 group-active:scale-95 transition-transform">
                  <Upload className="w-6 h-6 text-primary" />
                </div>
                <span className="text-sm font-semibold">
                  {photos.length > 0 ? 'Add more photos' : 'Tap to select photos'}
                </span>
                <span className="text-xs text-muted-foreground mt-1">Up to 10MB per file</span>

                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  className="hidden"
                  accept={ACCEPTED_IMAGE_TYPES.join(',')}
                  onChange={handleFileChange}
                />
              </div>

              {/* Selection List (Text Based) */}
              {photos.length > 0 && (
                <div className="space-y-2 w-full max-w-full overflow-hidden">
                  <div className="flex justify-between items-center text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
                    <span>Selected Files</span>
                    <span>{photos.length} items</span>
                  </div>
                  {/* Added overflow-hidden and w-full here */}
                  <ScrollArea className="h-[120px] w-full rounded-md border bg-muted/10 overflow-hidden">
                    <div className="p-2 space-y-1">
                      {photos.map((file, idx) => (
                        <div
                          key={`${file.name}-${idx}`}
                          className="flex items-center gap-2 bg-background p-2 rounded border text-sm w-full"
                        >
                          <FileImage className="w-4 h-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0 w-0">
                            <span className="truncate block font-medium" title={file.name}>
                              {file.name}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeFile(idx)}
                            className="text-muted-foreground hover:text-destructive p-1 shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
              <FormField control={form.control} name="photos" render={() => <FormMessage />} />
            </div>

            <DialogFooter className="bg-muted/30 p-4 flex flex-row gap-3 sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsOpen(false)}
                disabled={isUploading}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isUploading || !photos.length}
                className="flex-1 sm:min-w-[120px]"
              >
                {isUploading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading</>
                ) : (
                  `Upload ${photos.length || ''}`
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}